import React from 'react';
import { format } from 'date-fns';
import {
  BudgetRequest,
  BudgetRequestRecipientStatus,
  BudgetRequestRecipient,
} from '@models/budget.model';
import { useTranslation } from '@hooks/useTranslation';

interface CancelledBudgetsProps {
  cancelledBudgetRequests: BudgetRequest[];
}

export const CancelledBudgets: React.FC<CancelledBudgetsProps> = ({
  cancelledBudgetRequests,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  const formatCurrency = (amount?: number): string => {
    if (!amount) return tTaskDetail.budget.notSpecified;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date?: string | Date) => {
    if (!date) return '-';
    return format(new Date(date), 'MMM dd, yyyy');
  };

  if (cancelledBudgetRequests.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 border border-gray-300 rounded-lg">
      <div className="p-4 border-b border-gray-300">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
            {tTaskDetail.budget.cancelledBadge}
          </span>
          {cancelledBudgetRequests.length > 1
            ? tTaskDetail.budget.pastDelegationBids
            : tTaskDetail.budget.pastDelegationBid}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {cancelledBudgetRequests.length === 1
            ? tTaskDetail.budget.cancelledSingleMessage
            : tTaskDetail.budget.cancelledMultipleMessage.replace(
                '{n}',
                String(cancelledBudgetRequests.length),
              )}
        </p>
      </div>
      
      <div className="p-4 space-y-3">
        {cancelledBudgetRequests.map((cancelledBudget, index) => {
          const cancelledAcceptedRecipient = cancelledBudget.recipients?.find(
            (recipient: BudgetRequestRecipient) =>
              recipient.status === BudgetRequestRecipientStatus.ACCEPTED,
          );
          
          return (
            <div key={index} className="bg-white border border-gray-200 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {tTaskDetail.budget.cancelledDelegationTo}{' '}
                  {cancelledAcceptedRecipient?.recipientOrganization?.name ||
                    tTaskDetail.budget.unknown}
                </span>
                <span className="text-xs text-gray-500">
                  {tTaskDetail.budget.amount} {formatCurrency(cancelledBudget.maxBudgetAmount)}
                </span>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>{tTaskDetail.budget.start} {formatDate(cancelledBudget.requestedStartDate)}</span>
                <span>{tTaskDetail.budget.end} {formatDate(cancelledBudget.requestedEndDate)}</span>
                {cancelledAcceptedRecipient?.respondedAt && (
                  <span>{tTaskDetail.budget.accepted} {formatDate(cancelledAcceptedRecipient.respondedAt)}</span>
                )}
                {cancelledBudget.delegationCancelledAt && (
                  <span className="text-red-600">{tTaskDetail.budget.cancelled} {formatDate(cancelledBudget.delegationCancelledAt)}</span>
                )}
              </div>

              {cancelledBudget.delegateTaskId && (
                <div className="mt-1 text-xs text-gray-400">
                  {tTaskDetail.budget.delegatedTaskId} {cancelledBudget.delegateTaskId}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  window.open(
                    `/budgets?budgetRequestId=${cancelledBudget.budgetRequestId}`,
                    '_blank',
                  );
                }}
                className="mt-2 text-xs text-gray-600 underline hover:text-gray-800">
                {tTaskDetail.budget.viewDetails}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};